// Back and Forward, before Inertia sees them.
//
// Inertia keeps each page in history.state and, on popstate, restores the
// entry's page with preserveState: false, which remounts the page component
// (#524). That is right when Back leaves a page. It is wrong when the page is
// already on screen, and when leaving needs a question first:
// - Back that only closes an overlay (on native, useBackDismiss gives each
//   overlay its own entry);
// - Back from an entry an overlay closed by a tap left behind;
// - Back between a page's own entries, such as an in-page #anchor's;
// - Back or Forward out of a form with unsaved changes.
//
// The listener below is registered when this module first loads.
// entrypoints/inertia.jsx imports it before createInertiaApp, so it runs before
// Inertia's own listener and can keep an event from it. The steps run in a
// fixed order; each one that handles the event returns.

const navEntry = () => window.navigation?.currentEntry ?? null;

// The page part of the address: the hash changes within a page.
const pageHref = () => `${window.location.pathname}${window.location.search}`;

// An id for the entry on screen: the Navigation API's key where the browser
// has one (Inertia's replaceState keeps it), else an overlay entry's marker.
const entryId = () =>
  navEntry()?.key ?? window.history.state?.overlayId ?? null;

let shown = { href: null, index: null, id: null };

function noteShown() {
  shown = { href: pageHref(), index: navEntry()?.index ?? null, id: entryId() };
}

/** Call after changing the query of the entry on screen with replaceState. */
export function noteUrlChange() {
  shown.href = pageHref();
}

// Overlays whose entry is open, innermost last.
const openOverlays = [];
// The page of every entry an overlay pushed, open or left behind, by entry id.
const overlayEntries = new Map();
// Overlays Back asked to close that stayed open (their onClose refused).
const refused = new Set();

/**
 * Gives an overlay its own history entry, so Back closes it. Returns the
 * overlay to pass to releaseOverlayEntry when it closes any other way.
 */
export function pushOverlayEntry({ marker, close, isOpen }) {
  const href = pageHref();
  // Keep history.state: Inertia keeps the page there.
  window.history.pushState(
    { ...window.history.state, overlayId: marker },
    '',
    `#${marker}`,
  );
  const overlay = { marker, href, close, isOpen };
  openOverlays.push(overlay);
  noteShown();
  overlayEntries.set(shown.id, href);
  return overlay;
}

/**
 * An overlay closed by a tap, a swipe or an unmount. Its entry stays, hash
 * stripped without a popstate: history.back() would fire one, and a page
 * change started in the same moment could be undone by it.
 */
export function releaseOverlayEntry(overlay) {
  const index = openOverlays.indexOf(overlay);
  if (index !== -1) openOverlays.splice(index, 1);
  refused.delete(overlay);
  if (window.location.hash.includes(overlay.marker)) {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.href.split('#')[0],
    );
  }
}

// The unsaved-changes guard, set by FormGuardProvider.
const guard = {
  isDirty: null,
  onBlocked: null,
  onLost: null,
  // The form's address while the guard steps back to it.
  returningTo: null,
  // Without the Navigation API the direction of a traversal is unknown, so
  // the step back is a guess: 'back' first, then 'forward'.
  guessing: null,
  timer: null,
  // The traversal it stopped: the target's index where known, and the delta.
  stopped: { index: null, delta: -1 },
  allowNext: false,
};

export function setFormGuard({ isDirty, onBlocked, onLost }) {
  guard.isDirty = isDirty;
  guard.onBlocked = onBlocked;
  guard.onLost = onLost;
  return () => {
    guard.isDirty = null;
    guard.onBlocked = null;
    guard.onLost = null;
  };
}

function stepBackToForm(delta) {
  window.history.go(delta);
  clearTimeout(guard.timer);
  // history.go() to a missing entry does nothing and fires no popstate.
  guard.timer = setTimeout(onStepBackMissing, guard.guessing ? 1500 : 5000);
}

function stopReturning() {
  guard.returningTo = null;
  guard.guessing = null;
  clearTimeout(guard.timer);
}

// Guessing Back, the step forward found no entry: the user went Forward
// from the form, so it is one entry back. Past that, give up and show the
// page the address belongs to.
function onStepBackMissing() {
  if (guard.returningTo === null) return;
  if (guard.guessing === 'back') {
    guard.guessing = 'forward';
    guard.stopped = { index: null, delta: 1 };
    stepBackToForm(-1);
    return;
  }
  stopReturning();
  guard.onLost?.();
}

/** Discard: finish the traversal the guard stopped. */
export function completeStoppedTraversal() {
  guard.allowNext = true;
  const current = navEntry()?.index ?? null;
  const { index, delta } = guard.stopped;
  window.history.go(
    index !== null && current !== null ? index - current : delta,
  );
}

function onPopState(event) {
  const left = shown;
  noteShown();
  const stop = () => event.stopImmediatePropagation();
  const known = left.index !== null && shown.index !== null;

  // 1. The guard's own step back to the form: now ask.
  if (guard.returningTo !== null) {
    if (shown.href === guard.returningTo) {
      stopReturning();
      stop();
      guard.onBlocked?.();
      return;
    }
    // Guessing Back, the step forward reached another page: the user went
    // Forward from the form, which is two entries back from here.
    if (guard.guessing === 'back') {
      stop();
      guard.guessing = 'forward';
      guard.stopped = { index: null, delta: 1 };
      stepBackToForm(-2);
      return;
    }
    // Lost: let Inertia show the page this address belongs to.
    stopReturning();
    return;
  }

  // 2. Back that only closes the innermost overlay.
  const top = openOverlays.at(-1);
  if (top && !window.location.hash.includes(top.marker)) {
    openOverlays.pop();
    if (shown.href === top.href) stop();
    top.close();
    setTimeout(() => {
      if (top.isOpen()) refused.add(top);
    }, 0);
    return;
  }

  // 3. Back from an entry a closed overlay left behind lands on the same
  //    page, so nothing would seem to happen: carry on to the entry before.
  //    Not while an overlay that refused to close is still open: it has no
  //    entry, and Back should reach it.
  const leftPage = overlayEntries.get(left.id);
  if (
    left.id !== null &&
    leftPage === shown.href &&
    event.state?.page &&
    (!known || shown.index < left.index)
  ) {
    stop();
    if (refused.size) return;
    // At the tab's first entry Back has nowhere to go; the native shell
    // leaves the app, as Android's Back button does there.
    if (navEntry()?.index === 0) {
      window.dispatchEvent(new window.Event('bosko:history-start'));
    } else {
      window.history.back();
    }
    return;
  }

  // 4. Discard: let the stopped traversal through.
  if (guard.allowNext) {
    guard.allowNext = false;
    return;
  }

  // 5. Leaving a form with unsaved changes: step back to it, then ask.
  if (shown.href !== left.href && guard.isDirty?.()) {
    stop();
    guard.returningTo = left.href;
    if (known) {
      guard.guessing = null;
      guard.stopped = { index: shown.index, delta: shown.index - left.index };
      stepBackToForm(left.index - shown.index);
    } else {
      guard.guessing = 'back';
      guard.stopped = { index: null, delta: -1 };
      stepBackToForm(1);
    }
    return;
  }

  // 6. The page on screen, reached again through one of its own entries:
  //    don't let Inertia remount it, but do the rest of its restore: put the
  //    entry's scroll back (it set history.scrollRestoration to 'manual').
  //    A null state is an in-page #anchor jump, which Inertia handles
  //    without remounting.
  if (shown.href === left.href && event.state?.page) {
    stop();
    restoreScroll();
  }
}

// What Inertia's Scroll.restore does for a restored entry.
function restoreScroll() {
  window.requestAnimationFrame(() => {
    const saved = window.history.state ?? {};
    const position = saved.documentScrollPosition ?? { top: 0, left: 0 };
    window.scrollTo(position.left, position.top);
    const regions = saved.scrollRegions ?? [];
    document.querySelectorAll('[scroll-region]').forEach((region, i) => {
      if (regions[i]) region.scrollTo(regions[i].left, regions[i].top);
    });
  });
}

function onInertiaPage(event) {
  if (event.type === 'inertia:navigate') {
    noteShown();
  } else {
    // A replace visit keeps the entry; without the Navigation API its overlay
    // marker is gone from history.state, so keep the id we had.
    shown = {
      href: pageHref(),
      index: navEntry()?.index ?? null,
      id: navEntry()?.key ?? shown.id,
    };
  }
  // A replace visit to another page reuses the entry: it no longer belongs
  // to the overlay's page.
  const page = overlayEntries.get(shown.id);
  if (page !== undefined && page !== shown.href)
    overlayEntries.delete(shown.id);
}

if (typeof window !== 'undefined') {
  noteShown();
  window.addEventListener('popstate', onPopState);
  document.addEventListener('inertia:navigate', onInertiaPage);
  document.addEventListener('inertia:success', onInertiaPage);
}
